import React from 'react';
import { cn } from '../utils/cn';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface AvatarProps extends React.HTMLAttributes<HTMLDivElement> {
  src?: string | null;
  alt?: string;
  name?: string | null;
  size?: AvatarSize;
  fallbackIcon?: React.ReactNode;
  status?: 'online' | 'away' | 'offline';
}

const sizeMap: Record<AvatarSize, string> = {
  xs: 'h-7 w-7 text-label-sm',
  sm: 'h-9 w-9 text-body-md',
  md: 'h-11 w-11 text-title-md',
  lg: 'h-14 w-14 text-title-lg',
  xl: 'h-20 w-20 text-headline-sm',
};

function getInitials(name?: string | null): string {
  if (!name?.trim()) return '؟';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
}

export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  name,
  size = 'md',
  fallbackIcon,
  status,
  className,
  ...props
}) => {
  const [imageError, setImageError] = React.useState(false);
  const showImage = src && !imageError;
  const initials = getInitials(name);

  return (
    <div
      className={cn(
        'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary text-primary-foreground font-semibold ring-2 ring-primary/20',
        sizeMap[size],
        className,
      )}
      {...props}
    >
      {showImage ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={src!}
          alt={alt ?? name ?? ''}
          className="h-full w-full object-cover"
          onError={() => setImageError(true)}
          loading="lazy"
        />
      ) : fallbackIcon ? (
        fallbackIcon
      ) : (
        <span aria-hidden>{initials}</span>
      )}
      {status ? (
        <span
          className={cn(
            'absolute bottom-0 left-0 h-2.5 w-2.5 rounded-full ring-2 ring-surface',
            status === 'online' && 'bg-success',
            status === 'away' && 'bg-warning',
            status === 'offline' && 'bg-outline',
          )}
          aria-label={status}
        />
      ) : null}
    </div>
  );
};
